import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ExternalLink, Filter } from 'lucide-react';
import { allProjects, categories, getProjectsByCategory } from '../data/projects';

export default function PortfolioPage() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [showAllFilters, setShowAllFilters] = useState(false);

  const filteredItems = getProjectsByCategory(activeFilter);

  // İlk 5 kategori + "Daha Fazla" butonu
  const visibleCategories = showAllFilters ? categories : categories.slice(0, 5);

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-32" data-testid="portfolio-page">
      <div className="container-wide section-padding">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-mono text-pink-400 tracking-widest uppercase mb-4 block">
            Portföy
          </span>
          <h1 className="heading-section text-white mb-6">
            Büyüme Hikayeleri
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto mb-4">
            Stratejik düşünce ve nöro-pazarlama prensipleri ile oluşturduğumuz 
            <span className="text-white font-semibold"> {allProjects.length} başarılı proje</span> ve ölçülebilir sonuçlar.
          </p>
          <a 
            href="https://www.behance.net/medyatik" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 transition-colors"
          >
            <span>Behance'da tüm projelerimizi görün</span>
            <ExternalLink className="w-4 h-4" />
          </a>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-16"
        >
          <div className="flex flex-wrap justify-center gap-3" data-testid="portfolio-filters">
            {visibleCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveFilter(category.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                  activeFilter === category.id
                    ? 'bg-white text-black'
                    : 'bg-[#121212] text-zinc-400 border border-[#27272A] hover:border-violet-500/50 hover:text-white'
                }`}
                data-testid={`filter-${category.id}`}
              >
                {category.name}
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  activeFilter === category.id 
                    ? 'bg-black/20 text-black' 
                    : 'bg-zinc-800 text-zinc-500'
                }`}>
                  {category.count}
                </span>
              </button>
            ))}
            
            {!showAllFilters && categories.length > 5 && (
              <button
                onClick={() => setShowAllFilters(true)}
                className="px-5 py-2.5 rounded-full text-sm font-medium bg-[#121212] text-zinc-400 border border-[#27272A] hover:border-violet-500/50 hover:text-white transition-all duration-300 flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                Daha Fazla
              </button>
            )}
          </div>
          
          {/* Aktif filtre bilgisi */}
          <p className="text-center text-zinc-500 text-sm mt-6">
            {activeFilter === 'all' 
              ? `Toplam ${filteredItems.length} proje gösteriliyor`
              : `"${categories.find(c => c.id === activeFilter)?.name}" kategorisinde ${filteredItems.length} proje`
            }
          </p>
        </motion.div>

        {/* Portfolio Grid */}
        <motion.div 
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <AnimatePresence mode="popLayout">
            {filteredItems.map((item, index) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <Link 
                  to={`/proje/${item.slug}`}
                  className="group block relative bg-[#121212] border border-[#27272A] rounded-2xl overflow-hidden hover:border-violet-500/50 transition-colors duration-300"
                  data-testid={`portfolio-card-${item.id}`}
                >
                  {/* Image */}
                  <div className="aspect-[4/3] overflow-hidden">
                    <img 
                      src={item.image} 
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-mono text-violet-400">{item.categoryLabel}</span>
                      <div className="flex items-center gap-1">
                        <span className="font-mono text-lg font-bold text-emerald-400">{item.metric}</span>
                        <span className="text-xs text-zinc-500">{item.metricLabel}</span>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-violet-400 transition-colors line-clamp-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-zinc-500 mb-3">{item.client}</p>
                    <p className="text-sm text-zinc-400 leading-relaxed line-clamp-2">{item.description}</p>
                    
                    {/* View Project Link */}
                    <div className="mt-4 pt-4 border-t border-zinc-800">
                      <span className="text-sm text-violet-400 font-medium flex items-center gap-2 group-hover:gap-3 transition-all">
                        Detayları İncele
                        <ArrowUpRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>

                  {/* Hover Arrow */}
                  <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <ArrowUpRight className="w-5 h-5 text-white" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Behance CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mt-16"
        >
          <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-white mb-4">
              Daha Fazla Proje Görmek İster misiniz?
            </h3>
            <p className="text-zinc-400 mb-6">
              Tüm projelerimizi ve detaylı case study'leri Behance sayfamızda inceleyebilirsiniz.
            </p>
            <a 
              href="https://www.behance.net/medyatik" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-glow inline-flex items-center gap-2"
              data-testid="behance-link"
            >
              Behance'da Görüntüle
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mt-20"
        >
          <p className="text-zinc-400 mb-6">
            Sizin için de benzer sonuçlar elde etmek ister misiniz?
          </p>
          <Link 
            to="/iletisim" 
            className="btn-gradient inline-flex items-center gap-2"
            data-testid="portfolio-cta"
          >
            Büyüme Analizi Talep Et
            <ArrowUpRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
